import { MediumType } from '../common/RemoteResultCommand/index.types';

export type AiReadingStep = 'consent' | 'cameraPermission' | 'framing' | 'identify' | 'tariffs' | 'read' | 'confirm' | 'done';
export type AiReadingBlocker = 'permissionDenied' | 'noMatch' | 'candidates' | 'offline' | 'framingTimeout' | null;

// WHY the meter could not be assigned to a job — drives the specific wording of the noMatch
// panel and the spoken announcement ("that looks like a gas meter, but there is no gas job")
// instead of a generic "not found".
export type NoMatchReason = 'wrongMedium' | 'numberMismatch' | 'notRecognized';
export interface NoMatchInfo {
    reason: NoMatchReason;
    detectedNumber?: string;
    detectedMedium?: MediumType;
}

export interface AiReadingState {
    step: AiReadingStep;
    blocker: AiReadingBlocker;
    jobId?: string;
    meterNumber?: string;
    candidates: string[];
    // Set together with blocker 'noMatch'; cleared whenever the blocker resolves.
    noMatch: NoMatchInfo | null;
    stableCount: number;
    degraded: boolean;
}

export type AiReadingEvent =
    | { type: 'CONSENT_GIVEN' }
    | { type: 'CAMERA_READY' }
    | { type: 'CAMERA_DENIED' }
    | { type: 'METER_SEEN'; stableFrames: number }
    | { type: 'METER_LOST' }
    | { type: 'FRAMING_TIMEOUT' }
    | { type: 'RETRY_FRAMING' }
    | { type: 'METER_MATCHED'; jobId: string; meterNumber: string }
    | { type: 'MATCH_AMBIGUOUS'; candidates: string[] }
    | { type: 'MATCH_FAILED'; info?: NoMatchInfo }
    | { type: 'RETRY_IDENTIFY' }
    | { type: 'CANDIDATE_CHOSEN'; meterNumber: string }
    | { type: 'REGISTERS_COMPLETE' }
    | { type: 'FULL_READS_DONE' }
    | { type: 'CONFIRMED' }
    | { type: 'RESTART_FLOW' }
    | { type: 'WENT_OFFLINE' }
    | { type: 'BACK_ONLINE' }
    | { type: 'RETRY_CONNECTION' }
    | { type: 'TRACKER_DEGRADED' };

export const initialAiReadingState: AiReadingState = {
    step: 'consent',
    blocker: null,
    candidates: [],
    noMatch: null,
    stableCount: 0,
    degraded: false,
};

export const aiReadingReducer = (state: AiReadingState, event: AiReadingEvent): AiReadingState => {
    switch (event.type) {
        case 'CONSENT_GIVEN':
            return state.step === 'consent' ? { ...state, step: 'cameraPermission' } : state;
        case 'CAMERA_READY':
            return state.step === 'cameraPermission' ? { ...state, step: 'framing', blocker: null } : state;
        case 'CAMERA_DENIED':
            return state.step === 'cameraPermission' ? { ...state, blocker: 'permissionDenied' } : state;
        case 'METER_SEEN': {
            // The blocker guard covers in-flight live-loop results that resolve AFTER the framing
            // timeout fired — a late sighting must not advance the flow behind the timeout panel.
            if (state.step !== 'framing' || state.blocker) return state;
            const stableCount = state.stableCount + 1;
            if (stableCount >= event.stableFrames) {
                return { ...state, step: 'identify', stableCount: 0 };
            }
            return { ...state, stableCount };
        }
        case 'METER_LOST':
            return state.step === 'framing' ? { ...state, stableCount: 0 } : state;
        case 'FRAMING_TIMEOUT':
            // Nothing detected for the whole framing window: pause the live loop (via the blocker)
            // and surface the timeout panel. Other blockers (e.g. offline) take precedence.
            return state.step === 'framing' && !state.blocker ? { ...state, blocker: 'framingTimeout' } : state;
        case 'RETRY_FRAMING':
            return state.blocker === 'framingTimeout' ? { ...state, blocker: null, stableCount: 0 } : state;
        case 'METER_MATCHED':
            return state.step === 'identify' ? { ...state, step: 'tariffs', blocker: null, noMatch: null, jobId: event.jobId, meterNumber: event.meterNumber } : state;
        case 'MATCH_AMBIGUOUS':
            return state.step === 'identify' ? { ...state, blocker: 'candidates', candidates: event.candidates } : state;
        case 'MATCH_FAILED':
            return state.step === 'identify' ? { ...state, blocker: 'noMatch', noMatch: event.info ?? { reason: 'notRecognized' } } : state;
        case 'RETRY_IDENTIFY':
            // Back to framing (not identify): after "wrong meter" the user usually re-aims, so the
            // flow restarts at the alignment step with a clean stability counter.
            return state.blocker === 'noMatch' ? { ...state, step: 'framing', blocker: null, noMatch: null, stableCount: 0 } : state;
        case 'CANDIDATE_CHOSEN':
            return state.step === 'identify' ? { ...state, blocker: null, candidates: [], meterNumber: event.meterNumber } : state;
        case 'REGISTERS_COMPLETE':
            return state.step === 'tariffs' ? { ...state, step: 'read' } : state;
        case 'FULL_READS_DONE':
            return state.step === 'read' ? { ...state, step: 'confirm' } : state;
        case 'CONFIRMED':
            return state.step === 'confirm' ? { ...state, step: 'done' } : state;
        case 'RESTART_FLOW':
            // "Capture another meter" from the done dialog: back to framing with everything
            // job-/meter-specific cleared. Consent was already given, the camera keeps running.
            return state.step === 'done'
                ? { ...state, step: 'framing', blocker: null, noMatch: null, candidates: [], jobId: undefined, meterNumber: undefined, stableCount: 0 }
                : state;
        case 'WENT_OFFLINE':
            return state.step === 'consent' || state.step === 'done' ? state : { ...state, blocker: 'offline' };
        case 'BACK_ONLINE':
            return state.blocker === 'offline' ? { ...state, blocker: null } : state;
        case 'RETRY_CONNECTION':
            // Manual "try again" from the offline panel: clear the blocker so the live loop resumes.
            // If the connection is still down the next failed requests re-raise the offline blocker.
            return state.blocker === 'offline' ? { ...state, blocker: null } : state;
        case 'TRACKER_DEGRADED':
            return state.degraded ? state : { ...state, degraded: true };
        default:
            return state;
    }
};
